// _utils/people_util.ts
import { People } from '../models/people';

/**
 * Determines the Bootstrap class to apply to a person's table row based on their status.
 * @param person The person object with a hiring_date and/or exit_date.
 * @returns A string representing the Bootstrap class ('table-warning', 'table-success', 'table-danger') or an empty string.
 */
export function getPersonHiringStatusClass(person: People): string {
  const today = new Date();

  // Case 1: The person has a future exit date (warning status)
  // This is the new, simplified logic.
  if (person.exit_date) {
    const exitDate = new Date(person.exit_date);
    if (exitDate >= today) {
      return 'table-warning';
    }
  }

  // Case 2: The person has already left the company (danger status)
  if (person.exit_date) {
    const exitDate = new Date(person.exit_date);
    if (exitDate < today) {
      return 'table-danger';
    }
  }

  // Case 3: The person has not yet joined the company (success status)
  if (person.hiring_date) {
    const hiringDate = new Date(person.hiring_date);
    if (hiringDate > today) {
      return 'table-success';
    }
  }

  // Case 4: Active person
  return '';
}

/**
 * Provides icon class and popover message for a person's status.
 * @param person The person object with hiring_date and exit_date.
 * @returns An object with icon, color and message properties.
 */
export function getPersonStatusIconAndMessage(person: People): { icon: string, color: string, message: string, date: Date } | null {
  const today = new Date();
  const hiringDate = person.hiring_date ? new Date(person.hiring_date) : null;
  const exitDate = person.exit_date ? new Date(person.exit_date) : null;

  // Case 1: Departure is imminent or future
  if (exitDate && exitDate >= today) {
    return { 
      icon: 'bi-box-arrow-right',
      color: 'text-warning',
      message: 'PEOPLE.STATUS_MESSAGE.DEPARTURE_SCHEDULED',
      date: exitDate
    };
  }

  // Case 2: Departure has already occurred
  if (exitDate && exitDate < today) {
    return {
      icon: 'bi-box-arrow-right',
      color: 'text-danger',
      message: 'PEOPLE.STATUS_MESSAGE.LEFT_COMPANY',
      date: exitDate
    };
  }
  
  // Case 3: Future arrival
  if (hiringDate && hiringDate > today) {
    return {
      icon: 'bi-box-arrow-in-left',
      color: 'text-success',
      message: 'PEOPLE.STATUS_MESSAGE.FUTURE_ARRIVAL',
      date: hiringDate
    };
  }

  return null;
}

export function getAllocationRateClass(person: People): string {
  const rate = person.total_allocation_rate || 0;

  if (rate < 50 || rate > 100) {
    return 'bg-danger';
  } else if (rate >= 90 && rate <= 100) {
    return 'bg-success';
  } else if (rate >= 50 && rate < 90) {
    return 'bg-warning';
  } else {
    return 'bg-primary';
  }
}